import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Button, Flex, Grid, Img, keyframes, Link, Skeleton, useToast } from '@chakra-ui/react';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { Contest } from 'models';
import routes from 'routes';

import { useLazyGetUserQuery } from 'api/auth';
import { useCreateFakeUserMutation, useLazyGetContestsByDateQuery, useLazyGetPrizesQuery } from 'api/contest';

import { useAppSelector } from 'hooks/app';
import useBoosters from 'hooks/useBoosters';

import ChatIcon from 'icons/chat.svg';

import CardButton from '../CardButton';
import ContestList from '../ContestList';
import ContestListSkeleton from '../ContestListSkeleton';
import DateFilter from '../DateFilter';
import FeedbackChat from '../FeedbackChat';
import PrizeBanner from '../PrizeBanner';
import Sponsor from './Sponsor';

const bounceAnimation = keyframes`
  0% {
    transform: translateY(1px) translateZ(0px) rotate(2deg) rotateY(5deg) rotateX(0);
  }
  25% {
    transform: translateY(-2px) translateZ(0) rotate(2deg) rotateY(-8deg) rotateX(0) ;
  }
  50% {
    transform: translateY(2px) translateZ(0) rotate(2deg) rotateY(9deg) rotateX(0) ;
  }
  75% {
    transform: translateY(-2px) translateZ(0) rotate(2deg) rotateY(-8deg) rotateX(0) ;
  }
  100% {
    transform: translateY(1px) translateZ(0px) rotate(2deg) rotateY(5deg) rotateX(0) ;
  }
`;

const AuthHome = () => {
  const [searchParams] = useSearchParams();
  const fromApp = searchParams.get('from') === 'app';
  const bracketsBaseUrl = process.env.REACT_APP_BRACKETS_LINK || 'https://over-under.clutchpoints.com/';
  const bracketsLink = `${bracketsBaseUrl}${fromApp ? '?from=app' : ''}`;
  const user = useAppSelector((store) => store.auth.user);
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedContest, setSelectedContest] = useState<Contest | null>(null);
  const [gotNewMessage, setGotNewMessage] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const { setBooster } = useBoosters({ contest: selectedContest });
  const [fetchContestsByDate, { data: contests, isLoading, isFetching }] = useLazyGetContestsByDateQuery();
  const [getPrizes, { data: prizes, isLoading: isPrizesLoading }] = useLazyGetPrizesQuery();
  const toast = useToast();
  const navigate = useNavigate();
  const setSelecteContestById = (contestId: number) => {
    const selected = contests?.find((c) => c?.id === contestId);
    if (selected) {
      setSelectedContest(selected);
    }
  };

  useEffect(() => {
    if (!(window as any).fcSettings) return;
    (window as any).fcSettings = {
      onInit: function () {
        (window as any).fcWidget.on('unreadCount:notify', function (resp: { count: number }) {
          setGotNewMessage(resp.count > 0);
        });
      },
    };
  }, [user]);

  useEffect(() => {
    fetchContestsByDate({ date: selectedDate });
  }, [selectedDate, fetchContestsByDate]);

  useEffect(() => {
    getPrizes({ limit: 10, offset: 0 });
  }, [getPrizes]);

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  useEffect(() => {
    setBooster(null);
  }, []);

  const onChatCloseHandler = () => {
    setShowChat(false);
  };

  const handleContestDuplicated = () => {
    fetchContestsByDate({ date: selectedDate });
  };

  return (
    <Box w="100%" h="100%" data-cy="auth-home">
      {/* <Sponsor /> */}
      <DateFilter selectedDate={selectedDate} onDateChange={handleDateChange} />

      {isFetching ? (
        <Box textAlign="center" color="white" p="10px 15px 20px">
          <ContestListSkeleton />
        </Box>
      ) : contests && contests.length > 0 ? (
        <ContestList onViewChange={setSelecteContestById} contests={contests} onContestDuplicated={handleContestDuplicated} />
      ) : (
        <Flex
          fontSize="14px"
          fontWeight="600"
          lineHeight="16.8px"
          opacity="0.8"
          textAlign="center"
          alignItems="center"
          justifyContent="center"
          p="10px 15px 20px"
        >
          No contests available for this date.
        </Flex>
      )}

      {prizes?.results && prizes.results.length > 0 && <PrizeBanner prizes={prizes.results} />}

      <Grid gridTemplateColumns="1fr 1fr" px="15px" py="6px" gap="12px">
        <CardButton
          backgroundImage="url(/home/preview_over-under.webp)"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
          overflow="hidden"
          position="relative"
          label={
            <Box as="span">
              PLAY
              <br />
              OVER UNDER
            </Box>
          }
          text="New Modes"
          onClick={() => {
            window.location.replace(bracketsLink);
          }}
        />
        <CardButton
          backgroundImage="url(/home/streak-background.png)"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
          label={
            <Box as="span">
              Get OP
              <br />
              Boosts
            </Box>
          }
          text={
            <Box as="span">
              Play {user?.sbcStreakCount === 0 || (user?.sbcStreakCount || 0) >= 5 ? '5' : 5 - (user?.sbcStreakCount || 0)} more
              <br />
              days to unlock
            </Box>
          }
          position="relative"
          onClick={() => {
            navigate(routes.streakPromo);
          }}
          style={{ perspective: '1000px' }}
        >
          <Img
            src="https://clutchpoints-store-media.s3.us-east-1.amazonaws.com/SBC+media/booster-icon-placeholders/sbc-streak-pack.webp"
            w="100px"
            h="114px"
            objectFit="contain"
            position="absolute"
            top="-10px"
            right="-14px"
            animation={`${bounceAnimation} 5s infinite ease-in-out`}
            style={{
              transformStyle: 'preserve-3d',
              backfaceVisibility: 'visible'
            }}
          />
        </CardButton>
        <CardButton
          label={
            <Box as="span">
              HELP &<br />
              FEEDBACK
            </Box>
          }
          text={
            <Flex alignItems="center" gap="6px" justifyContent="space-between">
              Chat with devs{' '}
              <Box position="relative">
                {gotNewMessage && (
                  <Flex
                    pos="absolute"
                    w="8px"
                    h="8px"
                    borderRadius="10px"
                    top="0px"
                    right="0px"
                    backgroundColor="#F83C3C"
                    zIndex="1"
                    justifyContent="center"
                    alignItems="center"
                    fontSize="12px"
                    textShadow={`0 -0.5px 0px rgba(0, 0, 0, 0.9)`}
                  />
                )}
                <Img src={ChatIcon} w="24px" h="24px" />
              </Box>
            </Flex>
          }
          onClick={() => {
            setShowChat(true);
            if ((window as any).fcWidget) {
              (window as any).fcWidget.open();
            }
          }}
        />

        <CardButton
          label="PLAY SBC IN THE APP"
          onClick={() => {
            window.open('https://clutchpoints.onelink.me/Hz48/sbc', '_blank');
          }}
        />
      </Grid>
      {showChat && user && <FeedbackChat user={user} openOnMount onClose={onChatCloseHandler} />}
    </Box>
  );
};

export default AuthHome;
